import { appendQualitySlide } from './reportQualityExport';
// Generate PowerPoint report for AI Maturity Assessment
import pptxgen from 'pptxgenjs';
import { aiBenchmark } from './maturityMetrics';

export const generateAIMaturityPptx = (results, fullResults) => {
  const pptx = new pptxgen();
  
  // Set presentation properties
  pptx.author = 'SAMANVAI';
  pptx.title = `AI Maturity Assessment - ${results.company_name}`;
  pptx.subject = 'AI Transformation Readiness Report';
  pptx.company = 'SAMANVAI';
  
  // Color scheme - Professional AI theme
  const colors = {
    primary: '6366F1',    // Indigo
    indigo: '6366F1',
    secondary: '8B5CF6',  // Purple
    accent: '0EA5E9',     // Sky blue
    dark: '1E1B4B',       // Indigo 950
    darkGray: '1F2937',   // Gray 800
    mediumGray: '4B5563', // Gray 600
    lightGray: 'F3F4F6',  // Gray 100
    white: 'FFFFFF',
    red: 'EF4444',
    orange: 'F97316',
    amber: 'F59E0B',
    green: '22C55E',
    emerald: '10B981'
  };
  
  const getScoreColor = (score) => {
    if (score < 1.5) return colors.red;
    if (score < 2.5) return colors.orange;
    if (score < 3.5) return colors.amber;
    if (score < 4.5) return colors.emerald;
    return colors.green;
  };

  const getStatusText = (score, benchmark) => {
    const gap = score - benchmark;
    if (gap < -0.8) return 'Critical Gap';
    if (gap < -0.3) return 'Below Average';
    if (gap < 0.3) return 'At Par';
    return 'Above Average';
  };

  const maturityLevels = [
    'AI-Ready Data Platform',
    'Predictive AI',
    'Generative AI',
    'AI Agents',
    'Agentic AI',
    'Physical AI'
  ];

  const data = fullResults || results;
  const benchmark = aiBenchmark(data);
  const overallScore = data.overall_score || results.overall_score || 2.0;

  // ============== Slide 1: Title Slide ==============
  const slide1 = pptx.addSlide();
  slide1.background = { color: colors.dark };
  
  // Logo/Brand
  slide1.addText('SAMANVAI', {
    x: 0.5, y: 0.4, w: 3, h: 0.5,
    fontSize: 24, bold: true, color: colors.primary
  });
  
  slide1.addText('Agentic AI for CXOs', {
    x: 0.5, y: 0.8, w: 3, h: 0.3,
    fontSize: 10, color: colors.accent
  });
  
  // Main title
  slide1.addText('AI Maturity Assessment', {
    x: 0.5, y: 2, w: 7, h: 1,
    fontSize: 44, bold: true, color: colors.white
  });
  
  // Subtitle
  slide1.addText('Executive Report', {
    x: 0.5, y: 2.9, w: 5, h: 0.5,
    fontSize: 20, color: colors.secondary
  });
  
  // Company name
  slide1.addText(data.company_name || results.company_name, {
    x: 0.5, y: 3.6, w: 6, h: 0.6,
    fontSize: 28, color: colors.accent
  });
  
  // Meta info
  slide1.addText(`${data.industry || results.industry} | ${data.company_size || results.company_size} | ${data.annual_revenue || results.annual_revenue}`, {
    x: 0.5, y: 4.3, w: 6, h: 0.4,
    fontSize: 12, color: 'A0AEC0'
  });
  
  // Date
  slide1.addText(`Assessment Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, {
    x: 0.5, y: 4.9, w: 5, h: 0.3,
    fontSize: 11, color: '718096'
  });
  
  // Overall Score Badge
  slide1.addShape('rect', {
    x: 7.3, y: 1.6, w: 2.2, h: 3,
    fill: { color: getScoreColor(overallScore) },
    line: { color: colors.white, width: 2 }
  });
  slide1.addText(overallScore.toFixed(1), {
    x: 7.3, y: 1.9, w: 2.2, h: 1.2,
    fontSize: 52, bold: true, color: colors.white, align: 'center'
  });
  slide1.addText('out of 5.0', {
    x: 7.3, y: 3.0, w: 2.2, h: 0.3,
    fontSize: 11, color: colors.white, align: 'center'
  });
  slide1.addText(data.maturity_level?.level || maturityLevels[Math.floor(overallScore)] || 'Predictive AI', {
    x: 7.3, y: 3.4, w: 2.2, h: 0.5,
    fontSize: 10, bold: true, color: colors.white, align: 'center', wrap: true
  });
  slide1.addText(getStatusText(overallScore, benchmark.avg), {
    x: 7.3, y: 4.0, w: 2.2, h: 0.3,
    fontSize: 9, color: colors.white, align: 'center'
  });

  // ============== Slide 2: Executive Summary ==============
  const slide2 = pptx.addSlide();
  
  // Header
  slide2.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.primary }
  });
  slide2.addText('Executive Summary', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  // Key Stats Row
  const stats = [
    { label: 'Your Score', value: overallScore.toFixed(1), color: getScoreColor(overallScore) },
    { label: 'Industry Average', value: String(benchmark.avg || 2.5), color: colors.mediumGray },
    { label: 'Top Performers', value: String(benchmark.top || 4.0), color: colors.emerald },
    { label: 'Gap to Average', value: (overallScore - (benchmark.avg || 2.5)).toFixed(2), color: overallScore >= (benchmark.avg || 2.5) ? colors.green : colors.red }
  ];
  
  stats.forEach((stat, idx) => {
    const x = 0.5 + idx * 2.4;
    slide2.addShape('rect', {
      x: x, y: 1.3, w: 2.2, h: 1.1,
      fill: { color: colors.lightGray }
    });
    slide2.addText(stat.label, {
      x: x, y: 1.4, w: 2.2, h: 0.3,
      fontSize: 9, color: colors.mediumGray, align: 'center'
    });
    slide2.addText(stat.value, {
      x: x, y: 1.65, w: 2.2, h: 0.6,
      fontSize: 24, bold: true, color: stat.color, align: 'center'
    });
  });
  
  // Executive Summary Text
  let summaryText = '';
  const execSummary = data.ai_analysis?.executive_summary;
  
  if (typeof execSummary === 'string' && execSummary.length > 50) {
    summaryText = execSummary.substring(0, 1500);
  } else {
    const level = data.maturity_level?.level || 'Predictive AI';
    const gapStatus = overallScore < (benchmark.avg || 2.5) ? 'below' : 'above';
    const gap = Math.abs(overallScore - (benchmark.avg || 2.5)).toFixed(2);
    
    summaryText = `${data.company_name || results.company_name} has achieved an AI Maturity score of ${overallScore.toFixed(1)}/5.0, placing the organization at the ${level} level. This score is ${gap} points ${gapStatus} the ${data.industry || results.industry} industry average of ${benchmark.avg || 2.5}.

The assessment reveals key areas for improvement in AI capabilities including data infrastructure, model operations, and organizational readiness. To advance to the next maturity level, focused investments in MLOps, talent development, and governance frameworks are recommended.

Based on the 12-month transformation roadmap, the organization can expect to improve its AI maturity score by 1.0-1.5 points while realizing an estimated ROI of 3-5x on AI investments.`;
  }
  
  slide2.addText(summaryText, {
    x: 0.5, y: 2.6, w: 9, h: 2.5,
    fontSize: 11, color: colors.darkGray, valign: 'top'
  });

  // ============== Slide 3: AI Maturity Ladder ==============
  const slide3 = pptx.addSlide();
  
  slide3.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.secondary }
  });
  slide3.addText('The AI Maturity Ladder', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  const levels = [
    { level: 0, name: 'AI-Ready Data Platform', desc: 'Building the data infrastructure foundation' },
    { level: 1, name: 'Predictive AI', desc: 'Using historical data to forecast outcomes' },
    { level: 2, name: 'Generative AI', desc: 'Reasoning, synthesis, and knowledge creation' },
    { level: 3, name: 'AI Agents', desc: 'Task decomposition and tool orchestration' },
    { level: 4, name: 'Agentic AI', desc: 'Multi-agent coordination with feedback loops' },
    { level: 5, name: 'Physical AI', desc: 'AI that senses, moves, and acts in the real world' }
  ];
  
  const currentLevel = Math.floor(overallScore);
  
  levels.forEach((lvl, idx) => {
    const y = 4.8 - idx * 0.65;
    const isCurrentLevel = idx === currentLevel;
    const isPastLevel = idx < currentLevel;
    
    // Level bar
    slide3.addShape('rect', {
      x: 0.5, y: y, w: 0.5 + (idx + 1) * 0.8, h: 0.55,
      fill: { color: isCurrentLevel ? colors.primary : isPastLevel ? colors.emerald : colors.lightGray }
    });
    
    // Level number
    slide3.addText(`L${idx}`, {
      x: 0.5, y: y + 0.1, w: 0.4, h: 0.35,
      fontSize: 10, bold: true, color: isCurrentLevel || isPastLevel ? colors.white : colors.mediumGray
    });
    
    // Level name
    slide3.addText(lvl.name, {
      x: 5.5, y: y, w: 2.5, h: 0.3,
      fontSize: 10, bold: true, color: isCurrentLevel ? colors.primary : colors.darkGray
    });
    
    // Description
    slide3.addText(lvl.desc, {
      x: 5.5, y: y + 0.28, w: 4, h: 0.25,
      fontSize: 8, color: colors.mediumGray
    });
    
    // Current position indicator
    if (isCurrentLevel) {
      slide3.addText('← YOU ARE HERE', {
        x: 1.3 + (idx + 1) * 0.8, y: y + 0.1, w: 1.5, h: 0.35,
        fontSize: 9, bold: true, color: colors.primary
      });
    }
  });

  // ============== Slide 4: 6 Dimensions Overview ==============
  const slide4 = pptx.addSlide();
  
  slide4.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.primary }
  });
  slide4.addText('6 Dimensions of AI Maturity', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  const dimensions = data.dimension_scores || results.dimension_scores || [];
  
  dimensions.forEach((dim, idx) => {
    const col = idx % 3;
    const row = Math.floor(idx / 3);
    const x = 0.5 + col * 3.2;
    const y = 1.2 + row * 2.4;
    
    const dimScore = dim.score || 2.0;
    const status = getStatusText(dimScore, benchmark.avg || 2.5);
    const statusColor = dimScore < (benchmark.avg || 2.5) - 0.3 ? colors.red : 
                        dimScore < (benchmark.avg || 2.5) + 0.3 ? colors.amber : colors.green;
    
    // Card background
    slide4.addShape('rect', {
      x: x, y: y, w: 3, h: 2.2,
      fill: { color: colors.white },
      line: { color: statusColor, width: 2 }
    });
    
    // Score circle
    slide4.addShape('ellipse', {
      x: x + 0.2, y: y + 0.2, w: 0.9, h: 0.9,
      fill: { color: getScoreColor(dimScore) }
    });
    slide4.addText(dimScore.toFixed(1), {
      x: x + 0.2, y: y + 0.35, w: 0.9, h: 0.6,
      fontSize: 16, bold: true, color: colors.white, align: 'center'
    });
    
    // Dimension name
    slide4.addText(dim.dimension_name || `Dimension ${idx + 1}`, {
      x: x + 1.2, y: y + 0.2, w: 1.7, h: 0.7,
      fontSize: 10, bold: true, color: colors.darkGray, valign: 'top'
    });
    
    // Status badge
    slide4.addText(status, {
      x: x + 1.2, y: y + 0.8, w: 1.5, h: 0.25,
      fontSize: 8, bold: true, color: statusColor
    });
    
    // Progress bar background
    slide4.addShape('rect', {
      x: x + 0.2, y: y + 1.3, w: 2.6, h: 0.15,
      fill: { color: colors.lightGray }
    });
    // Progress bar fill
    slide4.addShape('rect', {
      x: x + 0.2, y: y + 1.3, w: 2.6 * (dimScore / 5), h: 0.15,
      fill: { color: getScoreColor(dimScore) }
    });
    
    // Gap info
    const gapVal = (dimScore - (benchmark.avg || 2.5)).toFixed(2);
    slide4.addText(`Gap: ${gapVal} vs benchmark (${benchmark.avg || 2.5})`, {
      x: x + 0.2, y: y + 1.55, w: 2.6, h: 0.25,
      fontSize: 8, color: parseFloat(gapVal) < 0 ? colors.red : colors.green
    });
    
    // Maturity level
    slide4.addText(dim.maturity_level || maturityLevels[Math.floor(dimScore)] || 'Predictive AI', {
      x: x + 0.2, y: y + 1.85, w: 2.6, h: 0.25,
      fontSize: 8, color: colors.mediumGray
    });
  });

  // ============== Slides 5-7: Individual Dimension Details ==============
  // Create 2 slides with 3 dimensions each
  for (let slideNum = 0; slideNum < 2; slideNum++) {
    const dimSlide = pptx.addSlide();
    
    dimSlide.addShape('rect', {
      x: 0, y: 0, w: 10, h: 0.8,
      fill: { color: colors.secondary }
    });
    dimSlide.addText(`Dimension Analysis (${slideNum * 3 + 1}-${Math.min((slideNum + 1) * 3, 6)})`, {
      x: 0.5, y: 0.2, w: 9, h: 0.4,
      fontSize: 22, bold: true, color: colors.white
    });
    
    const dimsOnSlide = dimensions.slice(slideNum * 3, (slideNum + 1) * 3);
    
    dimsOnSlide.forEach((dim, idx) => {
      const y = 1 + idx * 1.65;
      const dimScore = dim.score || 2.0;
      const gapToBenchmark = (benchmark.avg || 2.5) - dimScore;
      
      // Get analysis data
      const dimAnalysis = data.ai_analysis?.dimension_analysis?.[dim.dimension_id] || 
                          (Array.isArray(data.ai_analysis?.dimension_analysis) 
                            ? data.ai_analysis?.dimension_analysis.find(d => d.dimension_name === dim.dimension_name || d.dimension === dim.dimension_name)
                            : null) || {};
      
      // AI-specific improvement recommendations based on dimension
      const getAISpecificRecommendations = (dimName, score) => {
        const name = dimName.toLowerCase();
        if (score < 2) {
          if (name.includes('strategy')) return ['Define AI vision aligned with business objectives', 'Create AI governance framework with ethics guidelines'];
          if (name.includes('data')) return ['Establish data quality baseline for AI readiness', 'Build data pipeline infrastructure for ML workflows'];
          if (name.includes('technology')) return ['Deploy foundational ML infrastructure (MLOps)', 'Evaluate cloud AI platforms for scalability'];
          if (name.includes('talent') || name.includes('people')) return ['Launch AI/ML upskilling program for tech teams', 'Hire core AI team: data scientists, ML engineers'];
          if (name.includes('process') || name.includes('operation')) return ['Identify high-impact AI use cases for automation', 'Implement ML experiment tracking and versioning'];
          if (name.includes('ethics') || name.includes('governance')) return ['Establish AI ethics review board', 'Create responsible AI policy framework'];
        } else if (score < 3) {
          if (name.includes('strategy')) return ['Expand AI portfolio with measurable KPIs', 'Integrate AI strategy with digital transformation'];
          if (name.includes('data')) return ['Implement feature store for ML models', 'Scale data governance for AI workloads'];
          if (name.includes('technology')) return ['Deploy AutoML for citizen data scientists', 'Implement real-time ML inference capabilities'];
          if (name.includes('talent') || name.includes('people')) return ['Build AI center of excellence', 'Expand AI literacy across business units'];
          if (name.includes('process') || name.includes('operation')) return ['Automate ML pipeline CI/CD', 'Scale successful POCs to production'];
          if (name.includes('ethics') || name.includes('governance')) return ['Implement bias detection in ML models', 'Deploy explainable AI (XAI) techniques'];
        } else {
          if (name.includes('strategy')) return ['Achieve AI-first operating model', 'Lead industry AI innovation'];
          if (name.includes('data')) return ['Implement real-time feature engineering', 'Deploy AI-powered data quality'];
          if (name.includes('technology')) return ['Deploy edge AI capabilities', 'Implement federated learning'];
          if (name.includes('talent') || name.includes('people')) return ['Develop AI thought leadership', 'Build external AI ecosystem partnerships'];
          if (name.includes('process') || name.includes('operation')) return ['Achieve autonomous ML operations', 'Implement self-healing AI systems'];
          if (name.includes('ethics') || name.includes('governance')) return ['Lead AI ethics standards', 'Publish responsible AI research'];
        }
        return [`Enhance ${dimName} capabilities`, 'Continue maturity advancement'];
      };
      
      // Header bar
      const headerColor = dimScore < (benchmark.avg || 2.5) - 0.3 ? colors.red : 
                          dimScore < (benchmark.avg || 2.5) + 0.3 ? colors.amber : colors.emerald;
      dimSlide.addShape('rect', {
        x: 0.5, y: y, w: 9, h: 0.5,
        fill: { color: headerColor }
      });
      
      // Score
      dimSlide.addText(dimScore.toFixed(1), {
        x: 0.6, y: y + 0.1, w: 0.6, h: 0.3,
        fontSize: 14, bold: true, color: colors.white
      });
      
      // Name
      dimSlide.addText(dim.dimension_name || `Dimension ${slideNum * 3 + idx + 1}`, {
        x: 1.3, y: y + 0.1, w: 5, h: 0.3,
        fontSize: 12, bold: true, color: colors.white
      });
      
      // Status
      dimSlide.addText(getStatusText(dimScore, benchmark.avg || 2.5), {
        x: 7.5, y: y + 0.1, w: 1.8, h: 0.3,
        fontSize: 10, color: colors.white, align: 'right'
      });
      
      // Content area
      dimSlide.addShape('rect', {
        x: 0.5, y: y + 0.5, w: 9, h: 1.05,
        fill: { color: colors.lightGray }
      });
      
      // Strengths
      const strengths = dimAnalysis.strengths ? 
        (Array.isArray(dimAnalysis.strengths) ? dimAnalysis.strengths : [dimAnalysis.strengths]).slice(0, 2) :
        ['Building foundational capabilities'];
      
      dimSlide.addText('Strengths:', {
        x: 0.6, y: y + 0.55, w: 2.8, h: 0.2,
        fontSize: 8, bold: true, color: colors.emerald
      });
      dimSlide.addText(strengths.map(s => `• ${typeof s === 'string' ? s : s.description || 'Identified'}`).join('\n'), {
        x: 0.6, y: y + 0.75, w: 2.8, h: 0.7,
        fontSize: 7, color: colors.darkGray, valign: 'top'
      });
      
      // Gaps
      const gaps = dimAnalysis.gaps ? 
        (Array.isArray(dimAnalysis.gaps) ? dimAnalysis.gaps : [dimAnalysis.gaps]).slice(0, 2) :
        gapToBenchmark > 0 ? [`${gapToBenchmark.toFixed(1)} points below benchmark`] : ['Minor optimizations needed'];
      
      dimSlide.addText('Gaps:', {
        x: 3.5, y: y + 0.55, w: 2.8, h: 0.2,
        fontSize: 8, bold: true, color: colors.red
      });
      dimSlide.addText(gaps.map(g => `• ${typeof g === 'string' ? g : g.description || 'Identified'}`).join('\n'), {
        x: 3.5, y: y + 0.75, w: 2.8, h: 0.7,
        fontSize: 7, color: colors.darkGray, valign: 'top'
      });
      
      // How to Improve - use AI-specific recommendations
      const improvements = dimAnalysis.recommendations ? 
        (Array.isArray(dimAnalysis.recommendations) ? dimAnalysis.recommendations : [dimAnalysis.recommendations]).slice(0, 2) :
        getAISpecificRecommendations(dim.dimension_name || '', dimScore);
      
      dimSlide.addText('How to Improve:', {
        x: 6.5, y: y + 0.55, w: 2.9, h: 0.2,
        fontSize: 8, bold: true, color: colors.primary
      });
      dimSlide.addText(improvements.map(r => `• ${typeof r === 'string' ? r : r.description || r.recommendation || 'Implement improvements'}`).join('\n'), {
        x: 6.5, y: y + 0.75, w: 2.9, h: 0.7,
        fontSize: 7, color: colors.darkGray, valign: 'top'
      });
    });
  }

  // ============== Slide 8: Industry Benchmarking ==============
  const slide8 = pptx.addSlide();
  
  slide8.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.emerald }
  });
  slide8.addText(`Industry Benchmarking - ${data.industry || results.industry}`, {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 26, bold: true, color: colors.white
  });
  
  // Benchmark comparison cards
  const benchmarkCards = [
    { label: 'Your Score', value: overallScore.toFixed(1), color: getScoreColor(overallScore), bg: colors.primary },
    { label: 'Industry Average', value: String(benchmark.avg || 2.5), color: colors.white, bg: colors.mediumGray },
    { label: 'Top Performers', value: String(benchmark.top || 4.0), color: colors.white, bg: colors.emerald },
    { label: 'Gap to Average', value: (overallScore - (benchmark.avg || 2.5)).toFixed(2), color: colors.white, bg: overallScore >= (benchmark.avg || 2.5) ? colors.green : colors.red }
  ];
  
  benchmarkCards.forEach((card, idx) => {
    const x = 0.5 + idx * 2.4;
    slide8.addShape('rect', {
      x: x, y: 1.3, w: 2.2, h: 1.4,
      fill: { color: card.bg }
    });
    slide8.addText(card.label, {
      x: x, y: 1.4, w: 2.2, h: 0.3,
      fontSize: 10, color: card.color, align: 'center'
    });
    slide8.addText(card.value, {
      x: x, y: 1.7, w: 2.2, h: 0.8,
      fontSize: 32, bold: true, color: card.color, align: 'center'
    });
  });
  
  // Competitive Position Text
  slide8.addText('Competitive Position Analysis', {
    x: 0.5, y: 3, w: 9, h: 0.4,
    fontSize: 14, bold: true, color: colors.darkGray
  });
  
  let competitiveText = '';
  const benchmarkData = data.ai_analysis?.benchmark_comparison;
  
  if (typeof benchmarkData === 'string') {
    competitiveText = benchmarkData.substring(0, 800);
  } else if (benchmarkData) {
    const parts = [];
    if (benchmarkData.competitive_position) parts.push(benchmarkData.competitive_position);
    if (benchmarkData.gap_analysis) parts.push(benchmarkData.gap_analysis);
    if (benchmarkData.industry_specific_ai_adoption_trends) parts.push(`Industry Trends: ${benchmarkData.industry_specific_ai_adoption_trends}`);
    competitiveText = parts.join(' ').substring(0, 800);
  }
  
  if (!competitiveText) {
    const gapStatus = overallScore >= (benchmark.avg || 2.5) ? 'at or above' : 'below';
    const gapVal = Math.abs(overallScore - (benchmark.avg || 2.5)).toFixed(2);
    competitiveText = `Your organization is currently ${gapStatus} the ${data.industry || results.industry} industry average by ${gapVal} points. The gap to top performers (${benchmark.top || 4.0}) is ${((benchmark.top || 4.0) - overallScore).toFixed(2)} points. Industry trends show increasing adoption of AI capabilities across organizations in this sector, with leaders investing heavily in MLOps, talent development, and AI governance.`;
  }
  
  slide8.addShape('rect', {
    x: 0.5, y: 3.4, w: 9, h: 1.8,
    fill: { color: colors.lightGray }
  });
  slide8.addText(competitiveText, {
    x: 0.6, y: 3.5, w: 8.8, h: 1.6,
    fontSize: 10, color: colors.darkGray, valign: 'top'
  });

  // ============== Slide 9: 12-Month Transformation Roadmap ==============
  const slide9 = pptx.addSlide();
  
  slide9.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.secondary }
  });
  slide9.addText('12-Month Transformation Roadmap', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 26, bold: true, color: colors.white
  });
  
  // Default roadmap phases - score-based specificity
  const getDefaultRoadmapForScore = (score) => {
    if (score < 2) {
      return [
        {
          phase_name: 'Foundation',
          duration: 'Months 1-3',
          focus_areas: ['AI Strategy & Vision', 'Data Infrastructure Assessment', 'Core Team Assembly'],
          key_initiatives: ['Define AI use case portfolio', 'Assess data readiness for ML', 'Hire key AI talent', 'Evaluate cloud AI platforms'],
          investment_range: '$75K - $200K'
        },
        {
          phase_name: 'Build',
          duration: 'Months 4-6',
          focus_areas: ['Data Pipeline Development', 'First ML Pilots', 'MLOps Foundation'],
          key_initiatives: ['Build feature engineering pipelines', 'Launch 2-3 AI proof of concepts', 'Deploy ML experiment tracking', 'Establish model governance'],
          investment_range: '$150K - $350K'
        },
        {
          phase_name: 'Scale',
          duration: 'Months 7-9',
          focus_areas: ['Production Deployment', 'Model Monitoring', 'Team Expansion'],
          key_initiatives: ['Deploy first models to production', 'Implement A/B testing framework', 'Scale data science team', 'Build AI product backlog'],
          investment_range: '$200K - $500K'
        },
        {
          phase_name: 'Optimize',
          duration: 'Months 10-12',
          focus_areas: ['Performance Optimization', 'Knowledge Transfer', 'ROI Measurement'],
          key_initiatives: ['Optimize model performance', 'Launch AI literacy program', 'Measure and communicate ROI', 'Plan next phase expansion'],
          investment_range: '$150K - $300K'
        }
      ];
    } else if (score < 3) {
      return [
        {
          phase_name: 'Accelerate',
          duration: 'Months 1-3',
          focus_areas: ['MLOps Maturity', 'AutoML Deployment', 'Advanced Analytics'],
          key_initiatives: ['Implement CI/CD for ML', 'Deploy AutoML platform', 'Build feature store', 'Expand use case portfolio'],
          investment_range: '$100K - $250K'
        },
        {
          phase_name: 'Industrialize',
          duration: 'Months 4-6',
          focus_areas: ['AI Platform Scaling', 'Real-time ML', 'Center of Excellence'],
          key_initiatives: ['Scale ML infrastructure', 'Deploy real-time inference', 'Establish AI CoE', 'Implement model marketplace'],
          investment_range: '$200K - $450K'
        },
        {
          phase_name: 'Innovate',
          duration: 'Months 7-9',
          focus_areas: ['Advanced AI Capabilities', 'NLP/Computer Vision', 'AI Products'],
          key_initiatives: ['Deploy NLP/CV solutions', 'Launch AI-powered products', 'Implement reinforcement learning', 'Build AI innovation lab'],
          investment_range: '$250K - $600K'
        },
        {
          phase_name: 'Transform',
          duration: 'Months 10-12',
          focus_areas: ['AI-First Culture', 'Ecosystem Partnerships', 'Industry Leadership'],
          key_initiatives: ['Achieve AI-first operations', 'Build external AI partnerships', 'Publish AI thought leadership', 'Plan GenAI integration'],
          investment_range: '$200K - $400K'
        }
      ];
    } else {
      return [
        {
          phase_name: 'Lead',
          duration: 'Months 1-3',
          focus_areas: ['GenAI Integration', 'Edge AI', 'Autonomous Systems'],
          key_initiatives: ['Deploy GenAI applications', 'Implement edge ML', 'Build autonomous workflows', 'Lead industry standards'],
          investment_range: '$150K - $350K'
        },
        {
          phase_name: 'Pioneer',
          duration: 'Months 4-6',
          focus_areas: ['AI Research', 'Federated Learning', 'AI Ethics Leadership'],
          key_initiatives: ['Conduct applied AI research', 'Deploy federated learning', 'Lead responsible AI initiatives', 'Patent AI innovations'],
          investment_range: '$300K - $700K'
        },
        {
          phase_name: 'Dominate',
          duration: 'Months 7-9',
          focus_areas: ['AI Monetization', 'Platform Business', 'Ecosystem Building'],
          key_initiatives: ['Launch AI-as-a-Service', 'Build AI ecosystem', 'Monetize AI capabilities', 'Establish AI partnerships'],
          investment_range: '$400K - $900K'
        },
        {
          phase_name: 'Transcend',
          duration: 'Months 10-12',
          focus_areas: ['AGI Preparation', 'Quantum ML', 'Next-Gen AI'],
          key_initiatives: ['Explore quantum computing', 'Prepare for AGI implications', 'Build next-gen AI platform', 'Shape AI industry future'],
          investment_range: '$300K - $600K'
        }
      ];
    }
  };
  
  const defaultRoadmap = getDefaultRoadmapForScore(overallScore);
  
  let roadmapData = data.ai_analysis?.transformation_roadmap || data.ai_analysis?.implementation_roadmap || data.ai_analysis?.roadmap;
  
  if (!roadmapData || (Array.isArray(roadmapData) && roadmapData.length === 0)) {
    roadmapData = defaultRoadmap;
  } else if (!Array.isArray(roadmapData)) {
    roadmapData = Object.values(roadmapData);
  }
  
  const phases = roadmapData.slice(0, 4);
  const phaseColors = [colors.primary, colors.secondary, colors.emerald, colors.amber];
  
  phases.forEach((phase, idx) => {
    const x = 0.5 + idx * 2.4;
    
    // Phase header
    slide9.addShape('rect', {
      x: x, y: 1.2, w: 2.2, h: 0.8,
      fill: { color: phaseColors[idx] }
    });
    slide9.addText(`Phase ${idx + 1}`, {
      x: x, y: 1.25, w: 2.2, h: 0.25,
      fontSize: 9, color: colors.white, align: 'center'
    });
    slide9.addText(phase.phase_name || phase.phase || phase.name || `Phase ${idx + 1}`, {
      x: x, y: 1.5, w: 2.2, h: 0.35,
      fontSize: 11, bold: true, color: colors.white, align: 'center'
    });
    
    // Duration
    slide9.addText(phase.duration || phase.timeline || phase.timeframe || '', {
      x: x, y: 2.05, w: 2.2, h: 0.25,
      fontSize: 9, color: phaseColors[idx], align: 'center'
    });
    
    // Content box
    slide9.addShape('rect', {
      x: x, y: 2.35, w: 2.2, h: 2.4,
      fill: { color: colors.lightGray }
    });
    
    // Focus areas
    const focusAreas = phase.focus_areas || phase.areas || phase.objectives || [];
    const focusText = (Array.isArray(focusAreas) ? focusAreas : [focusAreas]).slice(0, 3)
      .map(a => `• ${typeof a === 'string' ? a : a.name || a.area || 'Focus area'}`).join('\n');
    
    slide9.addText('Focus Areas:', {
      x: x + 0.1, y: 2.4, w: 2, h: 0.25,
      fontSize: 8, bold: true, color: phaseColors[idx]
    });
    slide9.addText(focusText, {
      x: x + 0.1, y: 2.65, w: 2, h: 1,
      fontSize: 8, color: colors.darkGray, valign: 'top'
    });
    
    // Investment
    const investment = phase.investment_range || phase.investment || phase.budget;
    if (investment) {
      slide9.addShape('rect', {
        x: x + 0.1, y: 4.35, w: 2, h: 0.3,
        fill: { color: phaseColors[idx] }
      });
      slide9.addText(investment, {
        x: x + 0.1, y: 4.38, w: 2, h: 0.25,
        fontSize: 8, bold: true, color: colors.white, align: 'center'
      });
    }
    
    // Arrow between phases
    if (idx < 3) {
      slide9.addText('→', {
        x: x + 2.1, y: 1.4, w: 0.4, h: 0.4,
        fontSize: 18, color: colors.mediumGray, align: 'center'
      });
    }
  });

  // ============== Slide 10: Key Initiatives & Next Steps ==============
  const slide10 = pptx.addSlide();
  
  slide10.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.primary }
  });
  slide10.addText('Key Initiatives & Next Steps', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 26, bold: true, color: colors.white
  });
  
  // Immediate Actions (0-3 months)
  slide10.addText('Immediate Actions (0-3 months)', {
    x: 0.5, y: 1.2, w: 4.5, h: 0.4,
    fontSize: 14, bold: true, color: colors.primary
  });
  
  const immediateActions = [
    'Conduct AI readiness audit and identify quick wins',
    'Establish AI governance framework and policies',
    'Identify 2-3 high-impact pilot use cases',
    'Begin building AI talent pipeline and training'
  ];
  
  immediateActions.forEach((action, idx) => {
    slide10.addShape('rect', {
      x: 0.5, y: 1.7 + idx * 0.55, w: 4.5, h: 0.45,
      fill: { color: idx % 2 === 0 ? colors.lightGray : colors.white }
    });
    slide10.addText(`${idx + 1}. ${action}`, {
      x: 0.6, y: 1.78 + idx * 0.55, w: 4.3, h: 0.35,
      fontSize: 9, color: colors.darkGray
    });
  });
  
  // Medium-term Goals (3-6 months)
  slide10.addText('Medium-term Goals (3-6 months)', {
    x: 5.2, y: 1.2, w: 4.5, h: 0.4,
    fontSize: 14, bold: true, color: colors.secondary
  });
  
  const mediumTermGoals = [
    'Launch pilot AI projects with measurable KPIs',
    'Implement MLOps pipeline for model deployment',
    'Train core team on AI/ML best practices',
    'Establish model monitoring and feedback loops'
  ];
  
  mediumTermGoals.forEach((goal, idx) => {
    slide10.addShape('rect', {
      x: 5.2, y: 1.7 + idx * 0.55, w: 4.5, h: 0.45,
      fill: { color: idx % 2 === 0 ? colors.lightGray : colors.white }
    });
    slide10.addText(`${idx + 1}. ${goal}`, {
      x: 5.3, y: 1.78 + idx * 0.55, w: 4.3, h: 0.35,
      fontSize: 9, color: colors.darkGray
    });
  });
  
  // Expected Outcomes
  slide10.addShape('rect', {
    x: 0.5, y: 4, w: 9, h: 1.2,
    fill: { color: colors.emerald }
  });
  slide10.addText('Expected Outcomes', {
    x: 0.6, y: 4.1, w: 8.8, h: 0.3,
    fontSize: 12, bold: true, color: colors.white
  });
  
  const targetLevel = Math.min(5, Math.ceil(overallScore) + 1);
  slide10.addText(`• Improve AI Maturity Score from ${overallScore.toFixed(1)} to ${Math.min(5, overallScore + 1.5).toFixed(1)} within 12 months
• Achieve Level ${targetLevel} (${maturityLevels[targetLevel] || 'Advanced'}) maturity capabilities
• Realize 3-5x ROI on AI investments through efficiency gains and new revenue streams
• Build sustainable AI capabilities with strong governance and talent foundation`, {
    x: 0.6, y: 4.45, w: 8.8, h: 0.7,
    fontSize: 9, color: colors.white
  });

  // ============== Slide 11: Level Progression - Journey to Next AI Level ==============
  const levelProgression = data.ai_analysis?.level_progression;
  if (levelProgression) {
    const slideLP = pptx.addSlide();
    
    // Header
    slideLP.addShape('rect', {
      x: 0, y: 0, w: 10, h: 1.1,
      fill: { color: colors.indigo }
    });
    slideLP.addText('Level Up: Journey to Next AI Maturity Level', {
      x: 0.5, y: 0.2, w: 9, h: 0.4,
      fontSize: 24, bold: true, color: colors.white
    });
    slideLP.addText('Time, Effort, Investment & Benefits Analysis', {
      x: 0.5, y: 0.6, w: 9, h: 0.3,
      fontSize: 11, color: 'FFFFFF'
    });

    // Current to Next Level Visual
    const currentLevel = levelProgression.current_level?.level_number || Math.floor(overallScore);
    const nextLevel = levelProgression.next_level?.level_number || Math.min(currentLevel + 1, 5);
    const currentLevelName = levelProgression.current_level?.level_name || maturityLevels[currentLevel] || 'Current';
    const nextLevelName = levelProgression.next_level?.level_name || 'Next Level';
    
    // Current Level Circle
    slideLP.addShape('ellipse', {
      x: 1.5, y: 1.3, w: 1.2, h: 1.2,
      fill: { color: colors.amber }
    });
    slideLP.addText(String(currentLevel), {
      x: 1.5, y: 1.55, w: 1.2, h: 0.7,
      fontSize: 28, bold: true, color: colors.white, align: 'center'
    });
    slideLP.addText('Current', {
      x: 1.2, y: 2.5, w: 1.8, h: 0.3,
      fontSize: 10, bold: true, color: colors.darkGray, align: 'center'
    });
    slideLP.addText(currentLevelName, {
      x: 1, y: 2.75, w: 2.2, h: 0.3,
      fontSize: 9, color: colors.mediumGray, align: 'center'
    });

    // Arrow with time
    slideLP.addShape('rect', {
      x: 3.2, y: 1.75, w: 3.6, h: 0.3,
      fill: { color: colors.indigo }
    });
    slideLP.addText(levelProgression.progression_requirements?.time_estimate || '9-18 months', {
      x: 3.2, y: 2.1, w: 3.6, h: 0.3,
      fontSize: 10, bold: true, color: colors.indigo, align: 'center'
    });

    // Next Level Circle
    slideLP.addShape('ellipse', {
      x: 7.3, y: 1.3, w: 1.2, h: 1.2,
      fill: { color: colors.indigo }
    });
    slideLP.addText(String(nextLevel), {
      x: 7.3, y: 1.55, w: 1.2, h: 0.7,
      fontSize: 28, bold: true, color: colors.white, align: 'center'
    });
    slideLP.addText('Target', {
      x: 7, y: 2.5, w: 1.8, h: 0.3,
      fontSize: 10, bold: true, color: colors.darkGray, align: 'center'
    });
    slideLP.addText(nextLevelName, {
      x: 6.8, y: 2.75, w: 2.2, h: 0.3,
      fontSize: 9, color: colors.indigo, align: 'center'
    });

    // Resource Requirements Grid
    const reqY = 3.2;
    const reqCards = [
      { label: 'TIME', value: levelProgression.progression_requirements?.time_estimate || '9-18 months', color: '3B82F6' },
      { label: 'MANPOWER', value: levelProgression.progression_requirements?.manpower_resources?.internal_fte || 'Not available', color: '8B5CF6' },
      { label: 'INVESTMENT', value: levelProgression.progression_requirements?.investment_required?.total_range || 'Not available', color: colors.indigo },
      { label: 'ROI', value: levelProgression.progression_requirements?.business_benefits?.quantified_value || 'Not available', color: colors.amber }
    ];

    reqCards.forEach((card, idx) => {
      const x = 0.5 + idx * 2.4;
      
      slideLP.addShape('rect', {
        x: x, y: reqY, w: 2.2, h: 1.2,
        fill: { color: colors.lightGray }
      });
      
      slideLP.addShape('rect', {
        x: x, y: reqY, w: 2.2, h: 0.35,
        fill: { color: card.color }
      });
      slideLP.addText(card.label, {
        x: x, y: reqY + 0.05, w: 2.2, h: 0.25,
        fontSize: 9, bold: true, color: colors.white, align: 'center'
      });
      
      slideLP.addText(card.value, {
        x: x + 0.1, y: reqY + 0.45, w: 2, h: 0.65,
        fontSize: 11, bold: true, color: card.color, align: 'center', valign: 'middle'
      });
    });

    // Business Benefits Section
    const benY = 4.6;
    slideLP.addText('Business Benefits at Next AI Level', {
      x: 0.5, y: benY, w: 9, h: 0.35,
      fontSize: 12, bold: true, color: colors.indigo
    });

    const benefits = [
      { label: 'Efficiency Gains', value: levelProgression.progression_requirements?.business_benefits?.efficiency_gains || 'Not available' },
      { label: 'Revenue Impact', value: levelProgression.progression_requirements?.business_benefits?.revenue_impact || 'Not available' },
      { label: 'Cost Reduction', value: levelProgression.progression_requirements?.business_benefits?.risk_reduction || 'Not available' }
    ];

    benefits.forEach((ben, idx) => {
      const x = 0.5 + idx * 3.1;
      slideLP.addShape('rect', {
        x: x, y: benY + 0.4, w: 2.9, h: 0.7,
        fill: { color: 'EEF2FF' },
        line: { color: colors.indigo, width: 1 }
      });
      slideLP.addText(ben.label, {
        x: x + 0.1, y: benY + 0.45, w: 2.7, h: 0.25,
        fontSize: 8, color: colors.mediumGray
      });
      slideLP.addText(ben.value, {
        x: x + 0.1, y: benY + 0.7, w: 2.7, h: 0.35,
        fontSize: 10, bold: true, color: colors.indigo
      });
    });
  }

  // ============== Slide 12 (or 11 if no level progression): Thank You ==============
  const slideThankYou = pptx.addSlide();
  slideThankYou.background = { color: colors.dark };
  
  slideThankYou.addText('SAMANVAI', {
    x: 0.5, y: 0.4, w: 3, h: 0.5,
    fontSize: 24, bold: true, color: colors.primary
  });
  
  slideThankYou.addText('Thank You', {
    x: 0.5, y: 2, w: 9, h: 1,
    fontSize: 44, bold: true, color: colors.white, align: 'center'
  });
  
  slideThankYou.addText('Your AI Transformation Journey Starts Here', {
    x: 0.5, y: 3, w: 9, h: 0.5,
    fontSize: 18, color: colors.accent, align: 'center'
  });
  
  slideThankYou.addText('For questions about this assessment or to discuss next steps:', {
    x: 0.5, y: 3.8, w: 9, h: 0.4,
    fontSize: 12, color: 'A0AEC0', align: 'center'
  });
  
  slideThankYou.addText('www.samanvai.com', {
    x: 0.5, y: 4.3, w: 9, h: 0.5,
    fontSize: 18, color: colors.primary, align: 'center'
  });
  
  slideThankYou.addText('SAMANVAI - Agentic AI for CXOs', {
    x: 0.5, y: 4.9, w: 9, h: 0.4,
    fontSize: 11, color: '718096', align: 'center'
  });

  // Download the file
  const companyName = (data.company_name || results.company_name || 'Company').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
  const fileName = `AI_Maturity_Assessment_${companyName}_${new Date().toISOString().split('T')[0]}`;
  appendQualitySlide(pptx, fullResults || results);
  return pptx.writeFile({ fileName: fileName });
};

export default generateAIMaturityPptx;
